import { useState } from 'react';
import { Alert, Button, Card, CardContent, CardHeader, LinearProgress, Link, TextField, Typography } from '@mui/material';
import styles from './AuthScreen.module.css';
import { Localized } from '@fluent/react';
import LangSwitcher from './LangSwitcher';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { login } from '../../store/thunk/auth';
import { SessionStateUnauthenticated, RejectionReason } from '../../store/slices/session';
import { Link as RouterLink } from 'react-router-dom';

export default function AuthScreen() {
	const dispatch = useAppDispatch();
	const authState = useAppSelector(state => state.session);
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");

	if (authState.authenticated) {
		throw new Error("AuthScreen can only be shown when signed out.");
	}

	async function onSubmit() {
		dispatch(login(email, password));
	}

	return <AuthScreenUI
		authState={authState}
		email={email} password={password}
		setEmail={setEmail} setPassword={setPassword}
		onSubmit={onSubmit}
	/>;
}

interface AuthScreenUIProps {
	authState: SessionStateUnauthenticated;
	email: string;
	password: string;
	setEmail(password: string): void;
	setPassword(password: string): void;
	onSubmit(): void;
}

function AuthScreenUI(props: AuthScreenUIProps) {
	return (
		<form className={styles.root} onSubmit={(e) => {e.preventDefault(); props.onSubmit()}}>
			<Card className={styles.Card}>
				{props.authState.volatile.pending
					? <LinearProgress sx={{ height: "8px" }} />
					: <div style={{ height: "8px" }} />
				}

				<CardHeader
					title={
						<img style={{ display: 'block', margin: '8px auto 0 auto', height: '2em' }} src="https://mitberedskab.dk/images/logo.png" alt="Mit Beredskab" />
					}
				/>
				<CardContent>
					{props.authState.volatile.rejectionReason !== undefined &&
						<FormFeedback rejectionReason={props.authState.volatile.rejectionReason} />}
					<div>
						<Localized id="auth-screen-email" attrs={{ label: true }}>
							<TextField
								value={props.email}
								onChange={e => props.setEmail(e.target.value)}
								disabled={props.authState.volatile.pending}
								required
								variant="outlined"
								margin="normal"
								sx={{ marginTop: 0 }}
								fullWidth />
						</Localized>
					</div>
					<div>
						<Localized id="auth-screen-password" attrs={{ label: true }}>
							<TextField 
								value={props.password}
								onChange={e => props.setPassword(e.target.value)}
								disabled={props.authState.volatile.pending}
								required
								variant="outlined"
								margin="normal"
								type="password"
								sx={{ marginBottom: 0 }}
								fullWidth />
						</Localized>
						<Link component={RouterLink} underline="hover" to="/forgot">
							<Localized id="forgot-password">
								<Typography sx={{fontSize: 14}} />
							</Localized>
						</Link>
					</div>
					<Localized id="auth-screen-submit">
						<Button
							type="submit"
							disabled={props.authState.volatile.pending}
							variant="contained"
							fullWidth={true}
							sx={{ marginTop: '16px', padding: '15.75px' }}
						/>
					</Localized>
				</CardContent>
				<CardContent className={styles.CardContent_LangSwitcher}>
					<LangSwitcher />
				</CardContent>
			</Card>
		</form>
	);
}

interface FormFeedbackProps {
	rejectionReason: RejectionReason;
}

function formFeedbackMsg(rejectionReason: RejectionReason): {stringID: string, severity: "error" | "success"} {
	switch (rejectionReason) {
		case RejectionReason.BadCredentials:
			return {
				stringID: "auth-screen-feedback-bad-credentials",
				severity: "error",
			};
		case RejectionReason.Bug:
			return {
				stringID: "auth-screen-feedback-bug",
				severity: "error",
			};
		case RejectionReason.Network:
			return {
				stringID: "auth-screen-feedback-network",
				severity: "error",
			};
		case RejectionReason.ExternalLogout:
			return {
				stringID: "auth-screen-feedback-external-logout",
				severity: "error",
			};
		case RejectionReason.Logout:
			return { 
				stringID: "auth-screen-feedback-logout", 
				severity: "success" 
			};
	}
}

function FormFeedback(props: FormFeedbackProps) {
	const response = formFeedbackMsg(props.rejectionReason);

	return (
		<Localized id={response.stringID}>
			<Alert severity={response.severity} sx={{ marginBottom: '24px' }} />
		</Localized>
	);
}
